'use client';

import { useCallback } from 'react';
import { MarginMode, PublicKey } from '@drift-labs/sdk';
import {
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import { COMMON_UI_UTILS, matchEnum } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';

export interface SubaccountSettingsUpdate {
	maxLeverage?: number; // Displayed as "Max Leverage"
	marginMode?: MarginMode; // High leverage mode enabled/disabled
	marginTradingEnabled?: boolean; // Spot margin enabled
	reduceOnly?: boolean;
	pmm?: boolean;
}

/**
 * Hook for updating subaccount settings like margin mode, leverage, etc.
 */
export const useUpdateSubaccountSettings = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);

	/**
	 * Updates all subaccount flags / margin settings in one transaction
	 */
	const updateSubaccountSettings = useCallback(
		async (
			updates: Record<number, SubaccountSettingsUpdate>
		): Promise<boolean> => {
			const toastId = `updatingSettings_${Object.keys(updates).join(',')}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			const ixs: TransactionInstruction[] = [];

			try {
				await Promise.all(
					Object.keys(updates).map(async (key) => {
						const subAccountId = Number(key);
						// Must check if the key exists here because the value of maxLeverage will be set to undefined if the user is changing it to "no limit"
						if (Object.keys(updates[subAccountId]).includes('maxLeverage')) {
							const marginRatio = COMMON_UI_UTILS.convertLeverageToMarginRatio(
								updates[subAccountId].maxLeverage as number
							);
							ixs.push(
								(await driftClient?.getUpdateUserCustomMarginRatioIx(
									marginRatio as number,
									subAccountId as number
								)) as TransactionInstruction
							);
						}
						if (Object.keys(updates[subAccountId]).includes('marginMode')) {
							if (
								matchEnum(
									updates[subAccountId].marginMode,
									MarginMode.HIGH_LEVERAGE
								)
							) {
								ixs.push(
									(await driftClient?.getEnableHighLeverageModeIx(
										subAccountId
									)) as TransactionInstruction
								);
							} else {
								const subAccountKey =
									await driftClient?.getUserAccountPublicKey(subAccountId);
								ixs.push(
									(await driftClient?.getDisableHighLeverageModeIx(
										subAccountKey as PublicKey,
										driftClient?.getUserAccount(subAccountId)
									)) as TransactionInstruction
								);
							}
						}
						if (
							Object.keys(updates[subAccountId]).includes(
								'marginTradingEnabled'
							)
						) {
							ixs.push(
								(await driftClient?.getUpdateUserMarginTradingEnabledIx(
									updates[subAccountId].marginTradingEnabled as boolean,
									subAccountId
								)) as TransactionInstruction
							);
						}
						if (Object.keys(updates[subAccountId]).includes('reduceOnly')) {
							ixs.push(
								(await driftClient?.getUpdateUserReduceOnlyIx(
									updates[subAccountId].reduceOnly as boolean,
									subAccountId
								)) as TransactionInstruction
							);
						}
						if (Object.keys(updates[subAccountId]).includes('pmm')) {
							ixs.push(
								(await driftClient?.getUpdateUserProtectedMakerOrdersIx(
									subAccountId,
									updates[subAccountId].pmm as boolean
								)) as TransactionInstruction
							);
						}
					})
				);

				const tx = await driftClient?.buildTransaction(ixs);

				const txSig = await driftClient?.sendTransaction(
					tx as Transaction | VersionedTransaction
				);

				if (txSig) {
					notify({
						id: toastId,
						type: 'success',
						message: 'Updated margin settings.',
					});
					return true;
				} else {
					notify({
						id: toastId,
						type: 'error',
						message:
							'There was an error updating margin settings, please try again.',
					});
					return false;
				}
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[driftClient, walletAdapter]
	);

	return {
		updateSubaccountSettings,
	};
};

export default useUpdateSubaccountSettings;
